// src/components/map/hooks/adapters/useFacilityHighlightAdapter.ts
//
// Top-level adapter hook for the FacilityHighlighter component.
// Composes four sub-hooks (useSpiderifyLayer, useFacilityClickHandler,
// useContributionHighlight, useLocalNetworkMap) into a single unified
// interface that the thin UI shell consumes.
//
// Cross-cutting concerns (clearEverything, escape hotkey, resize observer,
// combined loading state) are wired here rather than in any sub-hook.
import useResizeObserver from "@react-hook/resize-observer";
import { useCallback, useRef, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";

import { DEFAULT_MIDSTREAM_SETTINGS } from "constants/app.constants";
import { useMapStore } from "stores/map";

import useFeatureFlags from "hooks/useFeatureFlags";

import { useUserSettingsDefaultsQuery } from "components/user/queries";

import { clearGeojsonSource } from "../../utils/geojsonSource";
import type { WellNetworkRequest } from "../useGetWellNetworkMap";
import { useMapContext } from "../useMapContext";
import type { LegendState } from "./useContributionHighlight";
import { useContributionHighlight } from "./useContributionHighlight";
import type { ClickedEntity } from "./useFacilityClickHandler";
import { useFacilityClickHandler } from "./useFacilityClickHandler";
import { useLocalNetworkMap } from "./useLocalNetworkMap";
import { useSpiderifyLayer } from "./useSpiderifyLayer";

// ── Constants ────────────────────────────────────────────────────────────────

const HIGHLIGHTED_WELLS = "highlighted-wells";
const HIGHLIGHTED_VERTICAL_WELLS = "highlighted-vertical-wells";
const HIGHLIGHTED_WELL_FACILITIES = "highlighted-well-facilities";
const HIGHLIGHTED_FACILITY = "highlighted-facility";
const CLICKED_FACILITY = "clicked-facility";
const CLICKED_WELL = "clicked-well";

// ── Types ────────────────────────────────────────────────────────────────────

export type { ClickedEntity } from "./useFacilityClickHandler";
export type { LegendState } from "./useContributionHighlight";

export interface UseFacilityHighlightAdapterReturn {
  // ── Click handler state ──────────────────────────────────────────
  /** The currently clicked entity (facility and/or well). */
  readonly clickedEntity: Maybe<ClickedEntity>;
  /** Whether the context menu is visible. */
  readonly showContextMenu: boolean;
  /** Setter for context menu visibility. */
  readonly setShowContextMenu: (show: boolean) => void;
  /** Context menu position in screen coordinates. */
  readonly contextMenuPosition: { readonly x: number; readonly y: number };
  /** Hide the context menu and clean up spider layers. */
  readonly hideContextMenu: () => void;

  // ── Contribution highlight state ─────────────────────────────────
  /** Current contribution legend state. */
  readonly legend: LegendState;
  /** Highlight wells contributing to the currently clicked facility. */
  readonly highlightContributionWells: () => void;
  /** Clear the contribution legend. */
  readonly clearLegend: () => void;
  /** Filter map to contributing wells for the clicked entity. */
  readonly filterToContributingWells: () => void;
  /** Filter map to facilities for the clicked well. */
  readonly filterToFacilities: () => Promise<void>;
  /** Filter to all contributing wells across all facilities. */
  readonly filterToAllContributingWellsFromAllFacilities: () => Promise<void>;
  /** Filter to the full network for all contributing wells. */
  readonly filterToNetworkForAllContributingWells: () => Promise<void>;
  /** Set midstream navigator filter flags before dispatching filters. */
  readonly setMidstreamFilterFlags: () => void;

  // ── Local network map state ──────────────────────────────────────
  /** Current well network map request parameters. */
  readonly wellNetworkMapRequest: WellNetworkRequest;
  /** Update the well network map request. */
  readonly setWellNetworkMapRequest: (request: WellNetworkRequest) => void;
  /** Network line width range [min, max]. */
  readonly networkLineWidth: readonly [number, number];
  /** Update the network line width. */
  readonly setNetworkLineWidth: (width: [number, number]) => void;
  /** Whether the network options panel should be visible. */
  readonly showNetworkVisualOptions: boolean;
  /** Well network map data from React Query. */
  readonly wellNetworkMap: unknown;
  /** Whether the network map data is currently fetching. */
  readonly isNetworkMapFetching: boolean;
  /** Trigger a well network map display for the clicked well. */
  readonly showWellNetworkMap: () => void;
  /** Clear all network map layers, sources, and related state. */
  readonly clearNetworkMap: () => void;
  /** The network depth legend state from the store. */
  readonly networkDepthLegend: {
    readonly title: string;
    readonly legendItems: ReadonlyArray<{
      readonly title: string;
      readonly color: string;
    }>;
    readonly visible: boolean;
  };

  // ── Cross-cutting ────────────────────────────────────────────────
  /** Clear all highlights, layers, legends, and reset clicked entity. */
  readonly clearEverything: () => void;
  /** Whether midstream data source is enabled in user settings. */
  readonly isMidstreamDataSourceEnabled: boolean;
  /** Feature flag accessor. */
  readonly hasFeature: (feature: string) => boolean;

  // ── Container dimensions (for DraggableLegendBase) ───────────────
  /** Ref to attach to the container element. */
  readonly containerRef: React.RefObject<HTMLDivElement>;
  /** Current container width. */
  readonly containerWidth: number;
  /** Current container height. */
  readonly containerHeight: number;

  // ── Zustand actions exposed for UI close handlers ────────────────
  /** Clear the Redux highlight state (deactivate midstream navigator button). */
  readonly clearHighlightContributingWellsForFacility: () => void;
  /** Clear the network depth legend from the store. */
  readonly clearNetworkDepthLegend: () => void;
  /** Reset the clicked entity to null. */
  readonly resetClickedEntity: () => void;
}

// ── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Top-level adapter hook for the FacilityHighlighter component.
 *
 * Composes four sub-hooks into a single interface:
 * - `useSpiderifyLayer`        — hover/spiderify visualization
 * - `useFacilityClickHandler`  — click interactions and context menu
 * - `useContributionHighlight` — well contribution highlighting and filters
 * - `useLocalNetworkMap`       — network map visualization
 *
 * Wires cross-cutting concerns:
 * - `clearEverything` spanning all sub-hooks
 * - Escape hotkey for global reset
 * - Resize observer for container dimensions
 * - Combined loading state dispatch
 * - User settings / beta feature flags
 */
export function useFacilityHighlightAdapter(): UseFacilityHighlightAdapterReturn {
  // ── Map context ────────────────────────────────────────────────────

  const { mapbox } = useMapContext();

  // ── Store actions ──────────────────────────────────────────────────

  const clearHighlightContributingWellsForFacility = useMapStore(
    (s) => s.clearHighlightContributingWellsForFacility
  );
  const clearNetworkDepthLegend = useMapStore((s) => s.clearNetworkDepthLegend);

  // ── User settings ──────────────────────────────────────────────────

  const { hasFeature } = useFeatureFlags();
  const userSettingsDefaultsQuery = useUserSettingsDefaultsQuery();
  const isMidstreamDataSourceEnabled =
    Object.values(userSettingsDefaultsQuery?.data?.MidstreamSettings ?? {})?.[0]
      ?.enabled ?? DEFAULT_MIDSTREAM_SETTINGS;

  // ── Container dimensions ───────────────────────────────────────────

  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);

  useResizeObserver(containerRef, (entry) => {
    setContainerHeight(entry.contentRect.height);
    setContainerWidth(entry.contentRect.width);
  });

  // ── Sub-hook 1: Spiderify ──────────────────────────────────────────

  const { restoreOriginalColors, removeAllSpiderLayers } = useSpiderifyLayer();

  // ── Sub-hook 2: Click handler ──────────────────────────────────────

  const {
    clickedEntity,
    setClickedEntity,
    showContextMenu,
    setShowContextMenu,
    contextMenuPosition,
    hideContextMenu
  } = useFacilityClickHandler({
    restoreOriginalColors,
    removeAllSpiderLayers
  });

  // ── Sub-hook 3: Contribution highlight ─────────────────────────────

  const {
    legend,
    highlightContributionWells,
    clearLegend,
    filterToContributingWells,
    filterToFacilities,
    filterToAllContributingWellsFromAllFacilities,
    filterToNetworkForAllContributingWells,
    setMidstreamFilterFlags
  } = useContributionHighlight({
    clickedEntity,
    setClickedEntity
  });

  // ── Sub-hook 4: Local network map ──────────────────────────────────

  const {
    wellNetworkMapRequest,
    setWellNetworkMapRequest,
    networkLineWidth,
    setNetworkLineWidth,
    showNetworkVisualOptions,
    wellNetworkMap,
    isNetworkMapFetching,
    showWellNetworkMap,
    clearNetworkMap,
    networkDepthLegend
  } = useLocalNetworkMap({
    clickedEntity
  });

  // ── Cross-cutting: clearEverything ─────────────────────────────────

  const clearEverything = useCallback(() => {
    clearLegend();
    clearGeojsonSource(mapbox, HIGHLIGHTED_WELL_FACILITIES);
    clearGeojsonSource(mapbox, HIGHLIGHTED_FACILITY);
    clearGeojsonSource(mapbox, HIGHLIGHTED_WELLS);
    clearGeojsonSource(mapbox, HIGHLIGHTED_VERTICAL_WELLS);
    clearGeojsonSource(mapbox, CLICKED_FACILITY);
    clearGeojsonSource(mapbox, CLICKED_WELL);
    clearNetworkMap();
    setClickedEntity({ facility: null, well: null });
  }, [mapbox, clearLegend, clearNetworkMap, setClickedEntity]);

  // ── Cross-cutting: Escape hotkey ───────────────────────────────────

  useHotkeys(
    "esc",
    () => {
      if (!mapbox) return;
      clearEverything();
      setClickedEntity(null);
    },
    { keyup: true },
    [mapbox, clearEverything, setClickedEntity]
  );

  // ── Cross-cutting: combined loading state ──────────────────────────

  // Note: individual sub-hooks dispatch their own loading state for their
  // specific data. The combined loading state here accounts for the
  // network map fetching in addition to the contribution data.
  // The contribution highlight sub-hook already dispatches its own
  // combined loading state for contribution + well fetching. The network
  // map sub-hook dispatches its own too. No extra dispatch needed here.

  // ── Convenience: resetClickedEntity ────────────────────────────────

  const resetClickedEntity = useCallback(() => {
    setClickedEntity(null);
  }, [setClickedEntity]);

  // ── Return ─────────────────────────────────────────────────────────

  return {
    // Click handler
    clickedEntity,
    showContextMenu,
    setShowContextMenu,
    contextMenuPosition,
    hideContextMenu,

    // Contribution highlight
    legend,
    highlightContributionWells,
    clearLegend,
    filterToContributingWells,
    filterToFacilities,
    filterToAllContributingWellsFromAllFacilities,
    filterToNetworkForAllContributingWells,
    setMidstreamFilterFlags,

    // Local network map
    wellNetworkMapRequest,
    setWellNetworkMapRequest,
    networkLineWidth,
    setNetworkLineWidth,
    showNetworkVisualOptions,
    wellNetworkMap,
    isNetworkMapFetching,
    showWellNetworkMap,
    clearNetworkMap,
    networkDepthLegend,

    // Cross-cutting
    clearEverything,
    isMidstreamDataSourceEnabled,
    hasFeature,

    // Container dimensions
    containerRef,
    containerWidth,
    containerHeight,

    // Zustand actions for UI close handlers
    clearHighlightContributingWellsForFacility,
    clearNetworkDepthLegend,
    resetClickedEntity
  };
}
